// Interactive corridor map (react-leaflet v4).
//
// Receives ALREADY-FILTERED arrays from the page — no filtering here.
// CircleMarker everywhere: Leaflet's default icon PNGs 404 under CRA bundling.
//
// Layering: crashes live in their own high-z pane so runs/clusters/halos can
// never sit on top of them (clicking a crash always opens ITS popup); the
// focus pulse and glare halos are non-interactive so they don't eat clicks.
//
// Focus protocol (row/strip/insight clicks): {lat,lng,zoom} or {bounds} flies
// the map; `crashId` / `runKey` / `intersectionId` additionally open that
// feature's popup once the fly-to settles.
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  MapContainer, TileLayer, LayersControl, LayerGroup, Pane, Polyline,
  CircleMarker, Circle, Popup, Tooltip, useMap,
} from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { findingSentence, fmtUsd, visibleFindings } from './insightsText';
import { recommendationForCrash, siteKeyOf, starsText } from './cmfBenefit';
import { Stars } from './CmfCard';
import { ManeuverButtons, ManeuverRows } from './ManeuverLinks';

const SEVERITY_RUN_COLORS = { severe: '#c92a2a', moderate: '#e8590c', minor: '#f5a623' };
// In "All analyses" mode runs are colored by ANALYSIS instead of severity so
// the mechanisms stay tellable-apart on one map.
const ANALYSIS_RUN_COLORS = {
  SSD: '#c92a2a', PSD: '#9c36b5', GLR: '#e8a30c', FSA: '#1971c2', HCA: '#e8590c',
  GRW: '#5f3dc4', ISD: '#0ca678',
};
const CRASH_COLORS = { fatal: '#7f0000', injury: '#e03131', pdo: '#868e96', unknown: '#adb5bd' };
const CRASH_RADIUS = { fatal: 8, injury: 6, pdo: 4.5, unknown: 4.5 };
const CORRIDOR_COLORS = ['#0c5c66', '#5b4b8a', '#7c5a2e', '#2e6b4f', '#7a3a4a', '#3a5a7a'];

function FitBounds({ bounds }) {
  const map = useMap();
  useEffect(() => {
    if (bounds) map.fitBounds(bounds, { padding: [30, 30] });
  }, [map, bounds]);
  return null;
}

// Row-click navigation: fly to a table row's segment/point, drop a pulse ring
// and — when the focus names a feature — open its popup after the move.
function FlyToFocus({ focus, crashRefs, runRefs, ixRefs }) {
  const map = useMap();
  useEffect(() => {
    if (!focus) return undefined;
    const target = focus.crashId != null ? crashRefs.current[focus.crashId]
      : focus.runKey ? runRefs.current[focus.runKey]
        : focus.intersectionId != null ? ixRefs.current[focus.intersectionId] : null;
    const open = () => { try { if (target && target.openPopup) target.openPopup(); } catch (e) { /* layer gone */ } };
    if (focus.bounds && focus.bounds.length > 1) {
      map.flyToBounds(focus.bounds, { padding: [60, 60], maxZoom: 16, duration: 0.8 });
    } else if (focus.lat != null) {
      map.flyTo([focus.lat, focus.lng], focus.zoom || 15, { duration: 0.8 });
    } else {
      open();
      return undefined;
    }
    if (target) map.once('moveend', open);
    return () => map.off('moveend', open);
  }, [map, focus, crashRefs, runRefs, ixRefs]);
  return null;
}

const fmtMp = (v) => (v === null || v === undefined ? '—' : v.toFixed(3));

// On-map legend for the active color scheme (minimizable).
function Legend({ isAll, showCrashes, hasIntersections, corridors }) {
  const [open, setOpen] = React.useState(true);
  if (!open) {
    return (
      <button type="button" className="safety-map-legend safety-legend-min"
        onClick={() => setOpen(true)} title="Show legend">Legend</button>
    );
  }
  return (
    <div className="safety-map-legend">
      <div className="lg-head">
        <span className="lg-title lg-title-first">{isAll ? 'Runs by analysis' : 'Runs by severity'}</span>
        <button type="button" className="lg-close" onClick={() => setOpen(false)}
          title="Minimize legend">–</button>
      </div>
      {isAll ? (
        Object.entries(ANALYSIS_RUN_COLORS).map(([a, c]) => (
          <div className="lg-row" key={a}><i className="lg-line" style={{ background: c }} />{a}</div>
        ))
      ) : (
        <>
          <div className="lg-row"><i className="lg-line" style={{ background: SEVERITY_RUN_COLORS.severe }} />Severe</div>
          <div className="lg-row"><i className="lg-line" style={{ background: SEVERITY_RUN_COLORS.moderate }} />Moderate</div>
          <div className="lg-row"><i className="lg-line" style={{ background: SEVERITY_RUN_COLORS.minor }} />Minor</div>
        </>
      )}
      {showCrashes && (
        <>
          <div className="lg-title">Crashes</div>
          <div className="lg-row"><i className="lg-dot" style={{ background: CRASH_COLORS.fatal }} />Fatal</div>
          <div className="lg-row"><i className="lg-dot" style={{ background: CRASH_COLORS.injury }} />Injury</div>
          <div className="lg-row"><i className="lg-dot" style={{ background: CRASH_COLORS.pdo }} />Property damage</div>
          <div className="lg-row"><i className="lg-dot lg-hollow" />Animal strike</div>
          <div className="lg-row"><i className="lg-dot lg-ring" style={{ borderColor: '#e8a30c' }} />Glare-time match</div>
          <div className="lg-row"><i className="lg-dot lg-ring" style={{ borderColor: '#6b2e8a' }} />Crash cluster</div>
        </>
      )}
      {hasIntersections && (
        <div className="lg-row"><i className="lg-dot" style={{ background: '#0ca678' }} />Intersection (ISD ring)</div>
      )}
      {(corridors || []).length > 1 && (
        <>
          <div className="lg-title">Corridors</div>
          {corridors.map((c, i) => (
            <div className="lg-row" key={c.key}>
              <i className="lg-line" style={{ background: CORRIDOR_COLORS[i % CORRIDOR_COLORS.length] }} />
              {c.road || c.key}{c.extent ? ` · MP ${c.extent[0]}–${c.extent[1]}` : ''}
            </div>
          ))}
        </>
      )}
    </div>
  );
}

export default function SafetyMap({ corridors, runs, crashes, clusters, intersections,
  analysis, focus, showCrashes = true, recommendations = [] }) {
  // corridors: [{key, road, P: corridorPayload, N: corridorPayload}]
  const crashRefs = useRef({});
  const runRefs = useRef({});
  const ixRefs = useRef({});
  // stable [lat, lng] per feature: react-leaflet compares `center` by reference
  // and calls setLatLng on every new array, which moves an OPEN popup during a
  // fly-to and crashes in Leaflet's _adjustPan (map null). Same array = no-op.
  const centers = useRef(new Map());
  const centerOf = useCallback((key, lat, lng) => {
    const prev = centers.current.get(key);
    if (prev && prev[0] === lat && prev[1] === lng) return prev;
    const next = [lat, lng];
    centers.current.set(key, next);
    return next;
  }, []);
  const bounds = useMemo(() => {
    const pts = (corridors || [])
      .flatMap((c) => [c.P, c.N].filter(Boolean))
      .flatMap((c) => c.points.map((p) => [p[0], p[1]]));
    return pts.length ? pts : null;
  }, [corridors]);

  const isAll = analysis === 'ALL';
  const runColor = (run) => (isAll
    ? (ANALYSIS_RUN_COLORS[run.analysis] || '#c92a2a')
    : (SEVERITY_RUN_COLORS[run.worstSeverity] || SEVERITY_RUN_COLORS.minor));
  const recByKey = useMemo(() => Object.fromEntries((recommendations || []).map((r) => [r.key, r])), [recommendations]);
  const nearRunCrashes = (run) => (crashes || []).filter((c) => (c.corridor || '') === (run.corridor || '')
    && c.milepost >= run.mpStart - 0.1 && c.milepost <= run.mpEnd + 0.1);

  if (!bounds) return <div className="safety-map-empty">Select a corridor to load the map.</div>;

  return (
    <div className="safety-map-holder">
      <MapContainer bounds={bounds} className="safety-map" scrollWheelZoom>
        <FitBounds bounds={bounds} />
        <FlyToFocus focus={focus} crashRefs={crashRefs} runRefs={runRefs} ixRefs={ixRefs} />

        {/* panes: crashes above everything clickable; decorations below */}
        <Pane name="runsPane" style={{ zIndex: 405 }} />
        <Pane name="clustersPane" style={{ zIndex: 420 }} />
        <Pane name="intersectionsPane" style={{ zIndex: 430 }} />
        <Pane name="crashesPane" style={{ zIndex: 460 }} />
        <Pane name="decorPane" style={{ zIndex: 470 }} />

        {focus && focus.lat != null && (
          <CircleMarker key={focus.key} center={[focus.lat, focus.lng]} radius={16} pane="decorPane"
            interactive={false}
            pathOptions={{ color: '#3b82f6', weight: 3, fillOpacity: 0.08, dashArray: '2 4' }} />
        )}

        <LayersControl position="topright">
          <LayersControl.BaseLayer checked name="Street (OSM)">
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" maxZoom={19} />
          </LayersControl.BaseLayer>
          <LayersControl.BaseLayer name="Satellite (Esri)">
            <TileLayer
              attribution="Imagery &copy; Esri, Maxar, Earthstar Geographics"
              url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
              maxZoom={19} />
          </LayersControl.BaseLayer>
          <LayersControl.BaseLayer name="Terrain (OpenTopoMap)">
            <TileLayer
              attribution='&copy; <a href="https://opentopomap.org">OpenTopoMap</a> (CC-BY-SA)'
              url="https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png" maxZoom={17} />
          </LayersControl.BaseLayer>

          <LayersControl.Overlay checked name="Surveyed centerline">
            <LayerGroup>
              {(corridors || []).map((c, i) => [c.P, c.N].map((dirPayload, j) =>
                dirPayload ? (
                  <Polyline key={`${c.key}-${j}`} positions={dirPayload.points.map((p) => [p[0], p[1]])}
                    pathOptions={{ color: CORRIDOR_COLORS[i % CORRIDOR_COLORS.length], weight: 3, opacity: 0.45 }}>
                    {(corridors || []).length > 1 && <Tooltip sticky>{c.road || c.key} · {j === 0 ? 'EB / P' : 'WB / N'}</Tooltip>}
                  </Polyline>
                ) : null))}
            </LayerGroup>
          </LayersControl.Overlay>

          <LayersControl.Overlay checked name={`Deficiency runs (${analysis})`}>
            <LayerGroup>
              {(runs || []).map((run) => {
                const key = siteKeyOf(run);
                const rec = recByKey[key];
                return (
                  <React.Fragment key={key}>
                    {/* white casing keeps the run legible over busy tiles */}
                    <Polyline positions={run.coords} pane="runsPane" interactive={false}
                      pathOptions={{ color: '#ffffff', weight: 8, opacity: 0.75 }} />
                    <Polyline positions={run.coords} pane="runsPane"
                      ref={(el) => { if (el) runRefs.current[key] = el; else delete runRefs.current[key]; }}
                      pathOptions={{ color: runColor(run), weight: 5, opacity: 0.95 }}>
                      <Tooltip sticky>
                        {isAll ? `${run.analysis} · ` : ''}
                        MP {fmtMp(run.mpStart)}–{fmtMp(run.mpEnd)} ({run.direction === 'P' ? 'EB' : 'WB'})
                        · {run.curveNumber != null
                          ? `curve #${run.curveNumber} · score ${run.worstValue}% · R=${Math.round(run.radiusFt || 0)} ft`
                          : `${run.nPoints} stations · worst ${run.worstValue}`}
                      </Tooltip>
                      <Popup>
                        <div className="safety-popup">
                          <strong>{run.analysis || analysis} deficiency · {run.direction === 'P' ? 'EB' : 'WB'}</strong>
                          <div className="safety-popup-mp">
                            {run.road ? `${run.road} · ` : ''}MP {fmtMp(run.mpStart)}–{fmtMp(run.mpEnd)} · {run.lengthMi} mi
                          </div>
                          <table><tbody>
                            <tr><td>Severity</td><td>{run.worstSeverity}</td></tr>
                            <tr><td>Worst</td><td>{run.curveNumber != null
                              ? `curve #${run.curveNumber} · score ${run.worstValue}% · R=${Math.round(run.radiusFt || 0)} ft`
                              : `${run.worstValue} (${run.nPoints} stations)`}</td></tr>
                            {showCrashes && (() => {
                              const near = nearRunCrashes(run);
                              const corr = near.filter((c) => c.findings && c.findings[run.analysis]);
                              return (
                                <tr><td>Crashes ±0.1 mi</td>
                                  <td>{near.length} ({near.filter((c) => !c.isAnimal).length} non-animal · {corr.length} correlated)</td></tr>
                              );
                            })()}
                          </tbody></table>
                          {rec?.entry && (
                            <div className="safety-popup-cmf">
                              ↳ <b>{rec.entry.name}</b> · CMF {rec.entry.cmf} <Stars entry={rec.entry} />
                              {' · '}{rec.preventedPerYr} prevented/yr · <span className="safety-benefit">{fmtUsd(rec.benefitPerYr)}/yr</span>
                            </div>
                          )}
                          {(run.reportUrl || (run.maneuvers || []).length > 0) && (
                            <div className="safety-popup-actions">
                              {/* ISD: one page per maneuver — link the ones that are short, worst first */}
                              <ManeuverButtons maneuvers={run.maneuvers} fallbackUrl={run.reportUrl}
                                fallbackLabel={`Open ${run.analysis || analysis} report at MP ${fmtMp(run.mpStart)}`} />
                            </div>
                          )}
                        </div>
                      </Popup>
                    </Polyline>
                  </React.Fragment>
                );
              })}
            </LayerGroup>
          </LayersControl.Overlay>

          {(intersections || []).length > 0 && (
            <LayersControl.Overlay checked name="Intersections (ISD)">
              <LayerGroup>
                {intersections.map((ix) => (
                  <React.Fragment key={`${ix.corridor || ''}-${ix.id}`}>
                    {ix.isdFt && (
                      <Circle center={centerOf(`ixr-${ix.corridor || ''}-${ix.id}`, ix.lat, ix.lng)} radius={ix.isdFt * 0.3048}
                        pane="intersectionsPane" interactive={false}
                        pathOptions={{ color: '#0ca678', weight: 1.5, fillOpacity: 0.04, dashArray: '3 4' }} />
                    )}
                    <CircleMarker center={centerOf(`ix-${ix.corridor || ''}-${ix.id}`, ix.lat, ix.lng)} radius={6} pane="intersectionsPane"
                      ref={(el) => { if (el) ixRefs.current[ix.id] = el; else delete ixRefs.current[ix.id]; }}
                      pathOptions={{ color: '#ffffff', weight: 1.5, fillColor: '#0ca678', fillOpacity: 0.95 }}>
                      <Tooltip>
                        Intersection {ix.id || '—'} · MP {fmtMp(ix.mp)}
                        {ix.isdFt ? ` · required ISD ${Math.round(ix.isdFt)} ft` : ''}
                        {ix.quality != null ? ` · quality ${(ix.quality * 100).toFixed(0)}%` : ''}
                      </Tooltip>
                      <Popup>
                        <div className="safety-popup">
                          <strong>{ix.type || 'Intersection'} {ix.id || ''}</strong>
                          <div className="safety-popup-mp">{ix.road ? `${ix.road} · ` : ''}MP {fmtMp(ix.mp)}</div>
                          <table><tbody>
                            {ix.isdFt ? <tr><td>Required ISD</td><td>{Math.round(ix.isdFt)} ft</td></tr> : null}
                            {ix.quality != null ? <tr><td>Worst quality</td><td>{(ix.quality * 100).toFixed(0)}%</td></tr> : null}
                            <ManeuverRows maneuvers={ix.maneuvers} />
                            {ix.mix && (
                              <>
                                <tr><td>Crashes ≤250 ft</td><td>{ix.nonAnimal} non-animal ({ix.crashes} total)</td></tr>
                                <tr><td>K/A/B/C/O</td><td>{ix.mix.K}/{ix.mix.A}/{ix.mix.B}/{ix.mix.C}/{ix.mix.O}</td></tr>
                                <tr><td>Cost · EPDO</td><td>{fmtUsd(ix.cost)} · {ix.epdo}</td></tr>
                              </>
                            )}
                          </tbody></table>
                          {(ix.reportUrl || (ix.maneuvers || []).length > 0) && (
                            <div className="safety-popup-actions">
                              <ManeuverButtons maneuvers={ix.maneuvers} fallbackUrl={ix.reportUrl}
                                fallbackLabel={`Open ISD report at MP ${fmtMp(ix.mp)}`} />
                            </div>
                          )}
                        </div>
                      </Popup>
                    </CircleMarker>
                  </React.Fragment>
                ))}
              </LayerGroup>
            </LayersControl.Overlay>
          )}

          {showCrashes && (
            <LayersControl.Overlay checked name="Crashes">
              <LayerGroup>
                {(crashes || []).map((c) => (
                  <React.Fragment key={`${c.corridor || ''}-${c.id}`}>
                    {/* amber halo: crash time inside glare periods (non-interactive
                        so it never blocks the click on the crash itself) */}
                    {c.glareMatch && (
                      <CircleMarker center={centerOf(`halo-${c.id}`, c.lat, c.lon)} radius={10} pane="crashesPane"
                        interactive={false}
                        pathOptions={{ color: '#e8a30c', weight: 2.5, fillOpacity: 0 }} />
                    )}
                    <CircleMarker
                      center={centerOf(`crash-${c.id}`, c.lat, c.lon)} pane="crashesPane"
                      ref={(el) => { if (el) crashRefs.current[c.id] = el; else delete crashRefs.current[c.id]; }}
                      radius={c.isAnimal ? 4 : CRASH_RADIUS[c.severityClass]}
                      pathOptions={
                        c.isAnimal
                          ? { color: '#9aa7ac', weight: 1.6, fillOpacity: 0 }
                          : { color: '#ffffff', weight: 1.2, fillColor: CRASH_COLORS[c.severityClass], fillOpacity: 0.95 }
                      }
                    >
                      <Popup>
                        <div className="safety-popup">
                          <strong>{c.manner || 'Crash'}</strong>
                          <div className="safety-popup-mp">
                            {c.road ? `${c.road} · ` : ''}MP {fmtMp(c.milepost)} · {c.city || '—'} · {c.datetime ? c.datetime.slice(0, 16).replace('T', ' ') : '—'}
                          </div>
                          <table><tbody>
                            <tr><td>Severity</td><td>{c.severity || '—'}</td></tr>
                            <tr><td>First event</td><td>{c.firstHarmful || '—'}</td></tr>
                            <tr><td>Light</td><td>{c.light || '—'}</td></tr>
                            <tr><td>Surface</td><td>{c.surface || '—'}</td></tr>
                            <tr><td>Junction</td><td>{c.junction || '—'}</td></tr>
                            <tr><td>Vehicles</td><td>{c.numVehicles ?? '—'}</td></tr>
                            <tr><td>Nearest run</td>
                              <td>{(() => { const m = c.nearestRunMi || {}; const v = analysis === 'ALL'
                                ? Object.entries(m).sort((a, b) => a[1] - b[1])[0]
                                : (m[analysis] != null ? [analysis, m[analysis]] : null);
                              return v ? `${v[1]} mi (${v[0]})` : '—'; })()}</td></tr>
                            {c.nearestIntersectionFt != null && (
                              <tr><td>Intersection</td>
                                <td>{c.nearestIntersectionFt} ft{c.insideIsd ? ' — inside required ISD' : ''}</td></tr>
                            )}
                          </tbody></table>
                          {visibleFindings(c, analysis).length > 0 && (
                            <div className="safety-popup-findings">
                              <strong>Deficiencies at this location</strong>
                              <ul>
                                {visibleFindings(c, analysis).map(([a, f]) => (
                                  <li key={a}>
                                    <b>{a}</b> — {findingSentence(a, f, c)}
                                    {f.reportUrl && (
                                      <> <a href={f.reportUrl} target="_blank" rel="noreferrer">Report ↗</a></>
                                    )}
                                    {(() => {
                                      const r = recommendationForCrash(recommendations, c, a);
                                      if (!r) return null;
                                      return (
                                        <div className="safety-popup-cmf">
                                          ↳ <b>{r.entry.name}</b> · CMF {r.entry.cmf}{' '}
                                          <span className="safety-stars">{starsText(r.entry.stars)}</span>
                                          {r.entry.fallback ? <span className="safety-fallback">3★</span> : null}
                                          {' · '}{r.matchCount} matching crash{r.matchCount === 1 ? '' : 'es'} at this site
                                          {' → '}{r.preventedPerYr} prevented/yr ·{' '}
                                          <span className="safety-benefit">{fmtUsd(r.benefitPerYr)}/yr</span>
                                        </div>
                                      );
                                    })()}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {c.isEmbankment && <em>Embankment / overturn event (GRW signature).</em>}
                          {c.isAnimal && <em>Animal strike — SSD correlation is case-dependent.</em>}
                          {(() => {
                            const links = Object.entries(c.findings || {}).filter(([, f]) => f?.reportUrl);
                            if (!links.length) return null;
                            return (
                              <div className="safety-popup-actions">
                                {links.map(([a, f]) => (
                                  <a key={a} className="safety-btn safety-report-btn" href={f.reportUrl} target="_blank" rel="noreferrer">
                                    Open {a} report at MP {fmtMp(c.milepost)} ↗
                                  </a>
                                ))}
                              </div>
                            );
                          })()}
                        </div>
                      </Popup>
                    </CircleMarker>
                  </React.Fragment>
                ))}
              </LayerGroup>
            </LayersControl.Overlay>
          )}

          {showCrashes && (
            <LayersControl.Overlay checked name="Crash clusters">
              <LayerGroup>
                {(clusters || []).map((cl, i) => (
                  <Circle key={i} center={centerOf(`cl-${cl.corridor || ''}-${cl.mpStart}`, cl.lat, cl.lng)} radius={200 + cl.count * 60}
                    pane="clustersPane"
                    pathOptions={{ color: '#6b2e8a', weight: 2.5, fillColor: '#6b2e8a', fillOpacity: 0.07, dashArray: '4 3' }}>
                    <Tooltip>
                      {cl.count} crashes · {cl.road ? `${cl.road} · ` : ''}MP {fmtMp(cl.mpStart)}
                      {cl.mpEnd > cl.mpStart ? `–${fmtMp(cl.mpEnd)}` : ''}
                    </Tooltip>
                  </Circle>
                ))}
              </LayerGroup>
            </LayersControl.Overlay>
          )}
        </LayersControl>
      </MapContainer>
      <Legend isAll={isAll} showCrashes={showCrashes}
        hasIntersections={(intersections || []).length > 0} corridors={corridors} />
    </div>
  );
}
